import { View } from 'react-native'
import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { LinearGradient } from 'expo-linear-gradient'
import { OrderPaymentStatus } from '@ca/common/types'
import { Collapsible } from '@ui/animated/Collapsible'
import Button from '@ui/buttons/Button'
import Icon, { IconName } from '@ui/elements/Icon'
import Typography from '@ui/typography'
import { colors } from '@ui/colors'
import { OrderPaymentStatusBadge } from '@order/OrderHistory/OrderBadge'

import { SectionProps } from '../types'
import { useRetryPayment } from '../useRetryPayment'
import { useOrderGroupPayments } from '../useOrderGroupPayments'
import type { GroupPaymentBanner } from '../useOrderGroupPayments'
import { usePriceFormatter } from 'hooks/useCurrency'

type BannerVariant = GroupPaymentBanner['variant']
type DisplayBanner = GroupPaymentBanner & { isOpen: boolean }

type BannerConfig = {
  gradient: readonly [string, string]
  icon: IconName
  accent: string
  cta: string
  badge?: OrderPaymentStatus
  showAllPayments: boolean
}

const bannerConfig: Record<BannerVariant, BannerConfig> = {
  partiallyPaid: {
    gradient: [colors.amber[600], colors.amber[800]],
    icon: 'alert-triangle',
    accent: colors.amber[600],
    cta: 'ui:payNow',
    showAllPayments: true,
  },
  declined: {
    gradient: [colors.red[600], colors.red[900]],
    icon: 'exclamation-circle',
    accent: colors.red[600],
    cta: 'order:details.sections.declined.retry',
    badge: 'declined',
    showAllPayments: true,
  },
  nonPayment: {
    gradient: [colors.amber[600], colors.amber[800]],
    icon: 'alert-triangle',
    accent: colors.amber[600],
    cta: 'order:details.sections.nonPayment.payNow',
    showAllPayments: false,
  },
}

type Props = SectionProps & {
  onShowPayments?: () => void
}

export default function PaymentBanner({ data, onShowPayments }: Props) {
  const { t } = useTranslation(['order', 'ui'])
  const formatter = usePriceFormatter()
  const { isPayable, banner, shouldHide } = useOrderGroupPayments(data)
  const { retry, isPending } = useRetryPayment(data)
  const [displayBanner, setDisplayBanner] = useState<DisplayBanner | null>(null)

  // Keep the last validated content visible while background refreshes disable Pay.
  if (
    banner &&
    (!displayBanner?.isOpen ||
      banner.variant !== displayBanner?.variant ||
      banner.remaining !== displayBanner?.remaining)
  ) {
    setDisplayBanner({ ...banner, isOpen: true })
  }
  if (shouldHide && displayBanner?.isOpen) {
    setDisplayBanner({ ...displayBanner, isOpen: false })
  }

  const variant = displayBanner?.variant ?? 'nonPayment'
  const config = bannerConfig[variant]
  const text = `order:details.sections.${variant}`

  return (
    <Collapsible open={displayBanner?.isOpen ?? false} fullWidth lazy>
      <View className="pt-8">
        <LinearGradient
          colors={config.gradient}
          start={{ x: 0, y: 0 }}
          end={{ x: 1, y: 0 }}
          style={{ borderRadius: 24 }}
          className="overflow-hidden"
        >
          <View className="flex flex-row items-start justify-center gap-8 p-8">
            <View className="h-6 w-8 items-center justify-center">
              <Icon name={config.icon} size={24} color={colors.white} />
            </View>
            <View className="flex flex-1 flex-col gap-2">
              <Typography variant="MDB" color={colors.white}>
                {t(`${text}.title`)}
              </Typography>
              <Typography variant="SM" color={colors.white75}>
                {t(`${text}.desc`, {
                  amount: formatter.format(displayBanner?.remaining ?? 0),
                })}
              </Typography>
              <View className="flex flex-row items-center gap-4 pt-6">
                <Button
                  size="sm"
                  label={t(config.cta)}
                  overrideColorBack={colors.white}
                  overrideColorFront={config.accent}
                  loading={isPending}
                  disabled={!isPayable || isPending}
                  onPress={retry}
                />
                {config.showAllPayments && onShowPayments && (
                  <Button
                    size="sm"
                    variant="outlined"
                    label={t('order:details.sections.declined.allPayments')}
                    className="border-white"
                    overrideColorFront={colors.white}
                    colors={['transparent', 'rgba(255,255,255,0.12)']}
                    onPress={onShowPayments}
                  />
                )}
              </View>
            </View>
            <View className="w-24 flex-row items-start justify-end self-stretch">
              {config.badge && (
                <OrderPaymentStatusBadge
                  status={config.badge}
                  className="self-start"
                />
              )}
            </View>
          </View>
        </LinearGradient>
      </View>
    </Collapsible>
  )
}
